import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { fetchProducts, Category } from '../services/stripe';
import { useRecentlyViewed } from '../context/RecentlyViewedContext';
import { ProductCard } from '../components/ProductCard';

const Home: React.FC = () => {
  const theme = useTheme();
  const { recentlyViewed } = useRecentlyViewed();
  console.log('Home - Recently Viewed:', recentlyViewed);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      try {
        setLoading(true);
        const data = await fetchProducts();
        if (mounted) {
          setCategories(data.categories);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load categories');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      setLoading(true); // Reset loading state for next mount
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: theme.palette.primary.main,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))',
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/hero-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.1)',
            transition: 'transform 0.3s ease-out',
            '&:hover': {
              transform: 'scale(1.15)',
            },
          }}
        />
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              maxWidth: 1000,
              mx: 'auto',
              px: 3,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                color: 'white',
                letterSpacing: '-0.02em',
              }}
            >
              Discover Your Perfect Match
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 6,
                lineHeight: 1.6,
                maxWidth: 800,
                mx: 'auto',
                fontWeight: 'normal',
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                color: 'white',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
              }}
            >
              Experience our collection of premium products designed for your intimate moments
            </Typography>
            <Button
              component={Link}
              to="/products"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: 'white',
                color: theme.palette.primary.main,
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha('#fff', 0.9),
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                },
                transition: 'all 0.3s ease',
                boxShadow: theme.shadows[4],
                borderRadius: '30px',
              }}
            >
              Shop Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Banner */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 3,
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
            >
              <Box
                component="img"
                src="//getsweetums.com/cdn/shop/files/RabbitIcon.png?v=1723316132&width=200"
                alt="Vegan icon"
                sx={{
                  width: 80,
                  height: 80,
                  mb: 2,
                  transition: 'all 0.3s ease',
                  filter: `brightness(0) saturate(100%) invert(27%) sepia(67%) saturate(2728%) hue-rotate(316deg) brightness(91%) contrast(97%)`,  
                  '&:hover': {
                    transform: 'scale(1.1)',
                    filter: `brightness(0) saturate(100%) invert(33%) sepia(29%) saturate(2211%) hue-rotate(295deg) brightness(84%) contrast(89%)`,  
                  },
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Vegan
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  maxWidth: 280,
                }}
              >
                Our products are vegan and never tested on animals
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 3,
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
            >
              <Box
                component="img"
                src="//getsweetums.com/cdn/shop/files/Asset_23_b7371cf1-0b6f-4e2e-a1c9-3ee8369b0c65.png?v=1723601471&width=200"
                alt="Ingredient Transparency icon"
                sx={{
                  width: 80,
                  height: 80,
                  mb: 2,
                  transition: 'all 0.3s ease',
                  filter: `brightness(0) saturate(100%) invert(27%) sepia(67%) saturate(2728%) hue-rotate(316deg) brightness(91%) contrast(97%)`,  
                  '&:hover': {
                    transform: 'scale(1.1)',
                    filter: `brightness(0) saturate(100%) invert(33%) sepia(29%) saturate(2211%) hue-rotate(295deg) brightness(84%) contrast(89%)`,  
                  },
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Ingredient Transparency
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  maxWidth: 280,
                }}
              >
                We are committed to using quality ingredients
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 3,
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                },
              }}
            >
              <Box
                component="img"
                src="//getsweetums.com/cdn/shop/files/Asset_25.png?v=1699885915&width=200"
                alt="Customer Service icon"
                sx={{
                  width: 80,
                  height: 80,
                  mb: 2,
                  transition: 'all 0.3s ease',
                  filter: `brightness(0) saturate(100%) invert(27%) sepia(67%) saturate(2728%) hue-rotate(316deg) brightness(91%) contrast(97%)`,  
                  '&:hover': {
                    transform: 'scale(1.1)',
                    filter: `brightness(0) saturate(100%) invert(33%) sepia(29%) saturate(2211%) hue-rotate(295deg) brightness(84%) contrast(89%)`,  
                  },
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                We Love Our Customers
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  maxWidth: 280,
                }}
              >
                We want you to be satisfied. We use humans, not bots, to provide customer service
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Categories Section */}
      <Box sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.03), py: 8 }}>
        <Container>
          <Typography
            variant="h4"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 700,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 4,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 2,
              },
            }}
          >
            Shop by Category
          </Typography>

          <Grid container spacing={4}>
            {categories.map((category) => (
              <Grid item key={category.name} xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: theme.shadows[10],
                    },
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height="260"
                      image={category.image}
                      alt={category.name}
                      sx={{
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                        p: 2,
                      }}
                    >
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                        {category.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {category.productCount} {category.productCount === 1 ? 'Product' : 'Products'}
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Button
                      component={Link}
                      to={`/products?category=${encodeURIComponent(category.name)}`}
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        mt: 2,
                        borderRadius: '25px',
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                    >
                      View Collection
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Recently Viewed Products Section */}
      {recentlyViewed && recentlyViewed.length > 0 && (
        <Box 
          sx={{ 
            mt: 8, 
            mb: 4,
            p: 4,
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              mb: 4,
              textAlign: 'center',
              fontWeight: 600,
              color: theme.palette.primary.main,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 60,
                height: 3,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 1,
              }
            }}
          >
            Recently Viewed Products
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2, // Add padding bottom for scrollbar
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[200],
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.primary.main,
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}
          >
            {recentlyViewed.map((product) => (
              <Box
                key={product.id}
                sx={{
                  minWidth: 280,
                  maxWidth: 280,
                  flexShrink: 0,
                }}
              >
                <ProductCard {...product} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Home;
